import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  // Dropdown state for Account menu - controlled by hover
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Dropdown refs for hover tracking
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isButtonOrMenu = buttonRef.current?.contains(event.target) || 
                             (dropdownRef.current && dropdownRef.current.contains(event.target));
      
      if (!isButtonOrMenu) {
        setAccountDropdownOpen(false);
        setShowPasswordModal(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Determine if user is logged in by checking if user exists
  const isAuthenticated = !!user;

  const handleLogout = () => {
    // Show confirmation dialog before logging out
    const confirmLogout = window.confirm('Logout?');
    
    if (confirmLogout) {
      logout();
      navigate('/');
    }
    // If user clicks "Cancel", do nothing and stay on current page
  };

  const handlePasswordChangeSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');

    // Validate passwords match
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match!');
      return;
    }

    // Validate new password is different from current
    if (passwordForm.currentPassword === passwordForm.newPassword) {
      setPasswordError('New password must be different from current password!');
      return;
    }

    try {
      setChangingPassword(true);
      
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        })
      });

      const data = await response.json();

      if (data.success) {
        alert('Password changed successfully!');
        setShowPasswordModal(false);
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setPasswordError('');
        
        // Reload page to update user data
        window.location.reload();
      } else {
        setPasswordError(data.message || 'Failed to change password. Please check your credentials.');
      }
    } catch (error) {
      console.error('Password change error:', error);
      setPasswordError('An error occurred while changing your password. Please try again.');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <nav className="bg-white shadow-md">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" className="text-2xl font-bold text-primary hover:text-blue-700 transition-colors">
            Classifieds<span className="text-secondary">.com</span>
          </Link>

          {/* Navigation Links */}
          <div className="flex items-center space-x-6">
            {isAuthenticated ? (
              <>
                <Link to="/create-listing" className="text-gray-700 hover:text-primary transition-colors font-medium">
                  Sell Item
                </Link>
                <Link to="/messages" className="text-gray-700 hover:text-primary transition-colors font-medium">
                  Messages
                </Link>

                {/* Account Dropdown - Hover opens, click toggles */}
                <div 
                  ref={dropdownRef}
                  className="relative account-menu-container inline-block"
                >
                  <button 
                    ref={buttonRef}
                    onClick={(e) => {
                      e.stopPropagation();
                      setAccountDropdownOpen(prev => !prev);
                    }}
                    onMouseEnter={() => setAccountDropdownOpen(true)}
                    className="text-gray-700 hover:text-primary transition-colors font-medium flex items-center space-x-1 outline-none"
                  >
                    <span>Account</span>
                    <svg 
                      className={`w-4 h-4 transform transition-transform ${accountDropdownOpen ? 'rotate-180' : ''}`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Dropdown Menu */}
                  {accountDropdownOpen && (
                    <div 
                      className="absolute right-0 mt-2 w-48 bg-white border rounded-lg shadow-xl z-[100] overflow-hidden"
                      onMouseEnter={() => setAccountDropdownOpen(true)}
                      onMouseLeave={() => {
                        const button = document.querySelector('.account-menu-container > button');
                        if (button && !button.matches(':hover')) {
                          setAccountDropdownOpen(false);
                        }
                      }}
                    >
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          window.location.href = '/profile';
                        }}
                        className="block px-4 py-2 text-gray-700 hover:bg-blue-50 transition-colors outline-none"
                      >
                        Profile
                      </button>
                      
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowPasswordModal(true);
                        }}
                        className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50 transition-colors outline-none"
                      >
                        Change Password
                      </button>
                      
                      <div className="border-t border-gray-200"></div>
                      
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLogout();
                        }}
                        className="block w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 transition-colors outline-none"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>

              </>
            ) : (
              <>
                <Link to="/login" className="text-gray-700 hover:text-primary transition-colors font-medium">
                  Login
                </Link>
                <Link 
                  to="/register" 
                  className="bg-primary text-white px-5 py-2 rounded-lg hover:bg-blue-800 transition-colors shadow-md"
                >
                  Register
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]"
          onClick={() => setShowPasswordModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-semibold mb-4 text-gray-800">Change Password</h3>
            
            {passwordError && (
              <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-3 rounded">
                <p className="text-sm text-red-700">{passwordError}</p>
              </div>
            )}

            <form onSubmit={handlePasswordChangeSubmit}>
              <div className="mb-4">
                <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  id="currentPassword"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  id="newPassword"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  disabled={changingPassword}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={changingPassword}
                  className={`px-4 py-2 text-white bg-primary hover:bg-blue-800 rounded-md transition-colors ${changingPassword ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {changingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;
